import { cn } from '../../lib/cn.ts';

export type TranscriptSpeaker = 'assistant' | 'user' | 'staff' | 'system';

export interface TranscriptSegment {
  id: string;
  speaker: TranscriptSpeaker;
  text: string;
  /** Optional timestamp label; when `onSeek` is set it becomes clickable. */
  at?: string;
}

export interface CallTranscriptProps {
  segments: TranscriptSegment[];
  /** When provided, clicking a segment's timestamp calls onSeek(at) so the
   *  transcript can drive an external player. */
  onSeek?: (at: string) => void;
  'data-testid'?: string;
}

const SPEAKER_LABEL: Record<TranscriptSpeaker, string> = {
  assistant: 'Assistant',
  user: 'Guest',
  staff: 'Staff',
  system: 'System',
};

/**
 * CallTranscript — a chat-style transcript. The guest (`user`) sits on the right
 * in a warm accent wash; assistant/staff/system sit on the left in a canvas
 * bubble with a mono speaker label above. Timestamps are mono and, when `onSeek`
 * is given, act as seek controls. Purely presentational.
 */
export function CallTranscript({
  segments,
  onSeek,
  'data-testid': testId = 'call-transcript',
}: CallTranscriptProps) {
  return (
    <div data-testid={testId} className="flex flex-col gap-4">
      {segments.map((segment) => {
        const isUser = segment.speaker === 'user';
        const label = SPEAKER_LABEL[segment.speaker];
        return (
          <div
            key={segment.id}
            data-testid={`call-transcript-row-${segment.id}`}
            className={cn('flex w-full flex-col gap-1', isUser ? 'items-end' : 'items-start')}
          >
            {!isUser ? (
              <span className="px-1 font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-warm-stone-deep)]">
                {label}
              </span>
            ) : null}
            <div
              className={cn(
                'flex max-w-[70%] flex-col gap-1 rounded-lg px-3.5 py-2.5',
                isUser
                  ? 'bg-[var(--color-accent-wash)] text-[var(--color-charcoal)]'
                  : 'bg-[var(--color-surface-2)] text-[var(--color-charcoal)]',
              )}
            >
              <p className="text-sm leading-relaxed">{segment.text}</p>
              {segment.at ? (
                onSeek ? (
                  <button
                    type="button"
                    onClick={() => onSeek(segment.at as string)}
                    className={cn(
                      'self-start font-[var(--font-mono)] text-[10px] tabular-nums text-[var(--color-stone-mid)]',
                      'cursor-pointer rounded-[var(--radius-xs)] hover:text-[var(--color-charcoal)]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-1',
                      isUser && 'self-end',
                    )}
                  >
                    {segment.at}
                  </button>
                ) : (
                  <span
                    className={cn(
                      'font-[var(--font-mono)] text-[10px] tabular-nums text-[var(--color-stone-mid)]',
                      isUser ? 'self-end' : 'self-start',
                    )}
                  >
                    {segment.at}
                  </span>
                )
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
